import { z } from "zod";
import type { ReasoningLevel } from "./model-parameters.ts";
import {
  INFERENCE_GEOGRAPHIES,
  isModelKey,
  MODEL_CATALOGUE,
  type ModelRole,
  modelImplicationsSchema,
} from "./models.ts";

/**
 * The user's effort choice (spec `model-choice`): a UI and API name over the roles users may be
 * given (`models.allowed` stores roles, ADR 0002). The server re-resolves it against the scope's
 * policy on every turn: a choice is a preference, never an authorization.
 */
export const EFFORTS = ["fast", "balanced", "deep"] as const;
export const effortSchema = z.enum(EFFORTS);
export type Effort = z.infer<typeof effortSchema>;

/** The role each effort's turn runs on. */
export const EFFORT_ROLE = {
  fast: "fast",
  balanced: "chat",
  deep: "reasoning",
} as const satisfies Record<Effort, ModelRole>;
export type EffortRole = (typeof EFFORT_ROLE)[Effort];

/**
 * The reasoning level a preset (or a conversation) asks for (model-picking D15, Q19): `default`
 * is Model default, the provider's own (no reasoning field is sent); the others map per model to
 * the provider's native setting (`callParameters`) and are ignored by models that do not reason.
 * Utility calls never reason.
 */
export const PRESET_REASONING_LEVELS = ["default", "low", "medium", "high"] as const;
export const presetReasoningSchema = z.enum(PRESET_REASONING_LEVELS);
export type PresetReasoning = z.infer<typeof presetReasoningSchema>;

/** A preset's level as ESI's reasoning level: Model default sends nothing, like `off`. */
export function presetLevel(level: PresetReasoning): ReasoningLevel {
  return level === "default" ? "off" : level;
}

/**
 * A preset level on a model supporting `levels` (D15): itself when supported, else the closest
 * supported level below it, else Model default. What the picker shows for a conversation's level.
 */
export function effectivePresetLevel(
  level: PresetReasoning,
  levels: readonly PresetReasoning[],
): PresetReasoning {
  const wanted = PRESET_REASONING_LEVELS.indexOf(level);
  return (
    levels.filter((l) => l !== "default" && PRESET_REASONING_LEVELS.indexOf(l) <= wanted).at(-1) ??
    "default"
  );
}

/**
 * The preset levels a catalogued version runs at (Q19): Model default always, then each level its
 * catalogue entry reasons at that the native setting can carry (`reasoning-config` never takes
 * `high`, see `callParameters`); `["default"]` alone for a version that does not reason or an
 * unknown key. The one rule the server, the picker and the admin screen share.
 */
export function reasoningLevelsOf(key: string | undefined): PresetReasoning[] {
  const support = key && isModelKey(key) ? MODEL_CATALOGUE[key].parameters.reasoning : null;
  return PRESET_REASONING_LEVELS.filter(
    (l) =>
      l === "default" ||
      (support !== null &&
        support.levels.includes(l) &&
        !(support.style === "reasoning-config" && l === "high")),
  );
}

/** A conversation's reasoning pick sent with a turn: a level, or `preset` to clear it. */
export const reasoningChoiceSchema = z.enum(["preset", ...PRESET_REASONING_LEVELS]);

/** The effort whose turn runs on `role`. */
export function effortOfRole(role: EffortRole): Effort {
  return (Object.keys(EFFORT_ROLE) as Effort[]).find((e) => EFFORT_ROLE[e] === role) ?? "balanced";
}

/** The order a refused choice or default falls back in (design D3); Balanced is the default. */
export const EFFORT_FALLBACK_ORDER: readonly Effort[] = ["balanced", "fast", "deep"];

/** A catalogue key: bounded, never a provider profile ID (an unknown key is ignored). */
export const modelChoiceKeySchema = z.string().trim().min(1).max(100);

/** A family ID (`anthropic/claude-opus`): bounded; an unknown family is ignored. */
export const familyChoiceSchema = z.string().trim().min(1).max(100);

/**
 * A provider a conversation picks for a lab (`esi`, `tenant:<kind>`, model-picking D5, D9):
 * bounded and shaped; re-checked every turn against the scope's choices, so a forged value
 * changes nothing.
 */
export const providerChoiceSchema = z
  .string()
  .max(40)
  .regex(/^(esi|tenant:[a-z0-9-]{1,32})$/);

/** Lab → provider picks: at most a handful of labs, lab IDs bounded. */
export const labProviderChoicesSchema = z
  .record(
    z
      .string()
      .min(1)
      .max(40)
      .regex(/^[a-z0-9-]+$/),
    providerChoiceSchema,
  )
  .refine((v) => Object.keys(v).length <= 16, "Too many labs");
export type LabProviderChoices = z.infer<typeof labProviderChoicesSchema>;

/**
 * The picker's state sent with a message (`sendMessageRequestSchema.options`). Absent: the
 * conversation's stored choice, else the default effort (older clients). `model` is read as its
 * family for one release (model-picking D9, Q9): users pick families, never versions.
 */
export const turnOptionsSchema = z.object({
  effort: effortSchema.optional(),
  /** @deprecated One release: read as its family. */
  model: modelChoiceKeySchema.optional(),
  family: familyChoiceSchema.optional(),
  /** A provider per lab for this conversation (D5, Q15); only among the scope's choices. */
  labProviders: labProviderChoicesSchema.optional(),
  /**
   * A reasoning level for this conversation (D15, Q19), or `preset` to clear it; re-checked
   * every turn (ignored when the preset levels are locked or the model does not reason).
   */
  reasoning: reasoningChoiceSchema.optional(),
});
export type TurnOptions = z.infer<typeof turnOptionsSchema>;

/**
 * A conversation's stored choice; null fields mean "not chosen". `model` holds a family ID (a
 * stored exact key from before slice 5 is read as its family and rewritten on the next turn).
 */
export const modelChoiceSchema = z.object({
  effort: effortSchema.nullable(),
  model: z.string().nullable(),
  /** Lab → the provider picked for this conversation; absent or empty: the scope's. */
  labProviders: z.record(z.string(), z.string()).optional(),
  /** The conversation's reasoning level (D15, Q19); absent or null: the preset's. */
  reasoning: presetReasoningSchema.nullable().optional(),
});
export type ModelChoice = z.infer<typeof modelChoiceSchema>;

/** A model as the picker offers it: key, display name and what using it implies here. */
export const modelOptionSchema = z.object({
  key: z.string(),
  displayName: z.string(),
  implications: modelImplicationsSchema,
  /** Where this scope's calls to it are processed (its first geography the policy allows). */
  geography: z.enum(INFERENCE_GEOGRAPHIES),
});
export type ModelOption = z.infer<typeof modelOptionSchema>;

/** A family as the picker names it (display name only, never a version list). */
export const familyRefSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  lab: z.string(),
  labName: z.string(),
});
export type FamilyRef = z.infer<typeof familyRefSchema>;

export const effortOptionSchema = z.object({
  effort: effortSchema,
  /** The effort's family in the scope (model-picking D9). Optional for one release. */
  family: familyRefSchema.optional(),
  /** The version the effort resolves to now, unless a family pick or a failover says otherwise. */
  model: modelOptionSchema,
  /** The scope pins this effort's family to a version (D7). */
  pinned: z.boolean().optional(),
  /** The pin is honoured (its version is healthy here); false: the info line. */
  pinHonoured: z.boolean().optional(),
  /**
   * The preset's reasoning level (D15, Q19) on the effort's version, lowered to what it supports,
   * the levels that version supports (`default` alone: it does not reason, no control), and
   * whether the levels are locked for the scope (no per-conversation level). Optional for one
   * release.
   */
  reasoning: z
    .object({
      level: presetReasoningSchema,
      /** The preset's level as set, before lowering (another family may run it as is). */
      preset: presetReasoningSchema.optional(),
      levels: z.array(presetReasoningSchema),
      locked: z.boolean(),
    })
    .optional(),
  /** @deprecated One release (the live SPA's Advanced list), sent only when family choice is on. */
  models: z.array(modelOptionSchema).optional(),
});
export type EffortOption = z.infer<typeof effortOptionSchema>;

/**
 * Another family users may pick (family choice on, D9): its resolved version and implications
 * for the effort's role, grouped by lab in the picker.
 */
export const familyOptionSchema = familyRefSchema.extend({
  model: modelOptionSchema,
  /** The efforts whose role this family can serve (the capability gate, D4). */
  efforts: z.array(effortSchema),
});
export type FamilyOption = z.infer<typeof familyOptionSchema>;

/** A provider of a lab as the picker offers it: display name only, never an account ID. */
export const providerOptionSchema = z.object({
  id: z.string(),
  displayName: z.string(),
});

/** A lab the scope offers, with its providers when users may pick one (D5, D9, Q15). */
export const labOptionSchema = z.object({
  lab: z.string(),
  displayName: z.string(),
  /** The scope's `choices` for the lab; one entry: nothing to pick. */
  providers: z.array(providerOptionSchema),
  /** The scope's provider: the default. */
  defaultProvider: z.string(),
  /** `models.labProvider` locked above: the choice is off. */
  locked: z.boolean(),
  /** The conversation's pick when it is still offered; null: the scope's. */
  picked: z.string().nullable(),
});
export type LabOption = z.infer<typeof labOptionSchema>;

/**
 * `GET …/model-options` (design D7): the efforts the caller's scope allows (each with at least
 * one usable model), the default, whether it is locked and by which level, and, when Advanced is
 * on, the exact models per effort. Keys and display names only, never profile IDs.
 */
export const modelOptionsSchema = z.object({
  catalogueVersion: z.string(),
  efforts: z.array(effortOptionSchema),
  defaultEffort: effortSchema,
  /** One effort only: the picker shows it locked. */
  locked: z.boolean(),
  /**
   * The level whose `models.allowed` leaves one effort; null when not locked, or when another
   * cause (residency, consent, allowed models) leaves one usable effort.
   */
  lockedBy: z.enum(["instance", "tenant", "entity", "project"]).nullable(),
  /** Family choice (`models.advancedPicker`, D9): users may pick another family. */
  advanced: z.boolean(),
  /** The other families users may pick, when family choice is on (optional for one release). */
  families: z.array(familyOptionSchema).optional(),
  /** The labs the scope offers, with their providers (optional for one release). */
  labs: z.array(labOptionSchema).optional(),
  /**
   * A conversation's stored choice and whether the next turn honors it as is; null outside a
   * conversation. `model` is the stored family.
   */
  choice: modelChoiceSchema.extend({ honored: z.boolean() }).nullable(),
});
export type ModelOptions = z.infer<typeof modelOptionsSchema>;
